import { useRouter } from "next/router";

import { SwitchCase } from "@enkor/toss-slash";

import { useAuthSession } from "@domains/auth/hooks/useAuthSession";
import { ToastError, ToastSuccess } from "@logics/utils/toast";
import { EVENT_MESSAGE } from "@domains/event/constants/toastMessages";
import { ERROR_MESSAGE } from "@domains/common/constants/toastMessages";
import { getUrlWithUTMParams } from "@logics/utils/makeUTMparameter";
import { DEFAULT_TITLE } from "@logics/tags/GeneralHead";
import shareHandler from "@logics/utils/shareHandler";
import { NAVIGATION_ROUTE } from "@domains/common/constants/navigationRoute";
import WebViewBridge from "@logics/utils/webViewBridge";
import { dataTrackEvent } from "@logics/utils/dataTrackEvent";
import { DATA_TRACKING_EVENT_NAME } from "@domains/common/constants/dataTrackEventName";
import EventViewModel from "@domains/event/viewModel/EventViewModel";
import { useFetchCategoryQuery } from "@domains/common/networks/category/categoryQueries";
import { CtaType, EventInfoTrackingDataType } from "@domains/event/types/event";
import { removeAmplitudeParams } from "@logics/utils/urlHandler";
import { isIOSAndMac } from "@logics/utils/isMobile";

import ParticipateButtonGroup from "./ParticipateButtonGroup/ParticipateButtonGroup";
import CancelButtonGroup from "./CancelButtonGroup/CancelButtonGroup";
import BaseButtonGroup from "./BaseButtonGroup/BaseButtonGroup";
import JoinedClosedButtonGroup from "./JoinedClosedButtonGroup/JoinedClosedButtonGroup";
import HostButtonGroup from "./HostButtonGroup/HostButtonGroup";
import HostClosedButtonGroup from "./HostClosedButtonGroup/HostClosedButtonGroup";

import * as S from "./EventDetailButton.style";

interface EventDetailButtonProps {
  chatId: string;
  eventId: string;
  eventInfo: EventViewModel;
  hostInfo: EventViewModel["host"];
  ctaType: CtaType;
  isEventClosed?: boolean;
}

const EventDetailButton = ({
  eventId,
  eventInfo,
  chatId,
  hostInfo,
  ctaType,
  isEventClosed,
}: EventDetailButtonProps) => {
  const { push } = useRouter();

  const { guardRoute } = useAuthSession();

  const { data: category } = useFetchCategoryQuery();

  const eventInfoData: EventInfoTrackingDataType = {
    eventId: eventInfo.id,
    eventCategory: category[eventInfo.categories[0]],
    eventTitle: eventInfo.title,
    eventImageNum: eventInfo.imageUrlList.length,
    eventFee: eventInfo.attendanceFee,
    eventDateTime: eventInfo.date,
    eventMaxParticipantCount: eventInfo.maxParticipantCount,
    eventParticipantCount: eventInfo.participantCount,
  };

  const handleClickShare = async () => {
    const url = getUrlWithUTMParams(window.location.toString(), {
      utm_source: "letsfip",
      utm_medium: "share_button",
      utm_campaign: eventId,
    });

    const updatedUrl = removeAmplitudeParams(url);

    if (WebViewBridge.isInAppWebView()) {
      WebViewBridge.getInstance().triggerShareEvent({
        text: DEFAULT_TITLE,
        subject: eventInfo.title,
        uri: updatedUrl,
        fileName: DEFAULT_TITLE,
        imageNames: eventInfo.imageUrlList,
        imagePaths: eventInfo.imageUrlList,
      });

      return;
    }

    const shareData = {
      title: DEFAULT_TITLE,
      url: updatedUrl,
    };

    const result = await shareHandler(shareData);

    if (result === "shared") {
      dataTrackEvent({
        eventName: DATA_TRACKING_EVENT_NAME.GUEST_EVENT.CLICK_SHARE,
        properties: {
          shareType: "system",
          ...eventInfoData,
        },
      });

      return;
    }
    if (result === "copiedToClipboard") {
      dataTrackEvent({
        eventName: DATA_TRACKING_EVENT_NAME.GUEST_EVENT.CLICK_SHARE,
        properties: { shareType: "clipboard", ...eventInfoData },
      });

      ToastSuccess(EVENT_MESSAGE.SHARE_LINK);

      return;
    }
    if (result === "failed") {
      ToastError(ERROR_MESSAGE.GENERAL);
    }
  };

  const handleClickViewEditionDetail = () => {
    if (!eventInfo?.project?.id) return;

    dataTrackEvent({
      eventName: DATA_TRACKING_EVENT_NAME.GUEST_EVENT.CLICK_EDITION_CTA,
    });

    if (WebViewBridge.isInAppWebView()) {
      WebViewBridge.getInstance().navigateToProjectDetail(eventInfo.project.id);
      return;
    }

    push({
      pathname: NAVIGATION_ROUTE.PROJECT_DETAIL.PATHNAME,
      query: { projectId: eventInfo.project.id },
    });
  };

  const handleClickEnterChatRoom = () =>
    guardRoute(() => {
      if (WebViewBridge.isInAppWebView()) {
        WebViewBridge.getInstance().navigateToChatRoom(
          Number(chatId),
          Number(eventId),
        );
      } else {
        push({
          pathname: `${NAVIGATION_ROUTE.CHAT_ROOM.HREF}/${chatId}`,
          query: { eventId },
        });
      }
    });

  return (
    <S.BottomButtonBox $shouldFixSafeArea={!isIOSAndMac()}>
      <SwitchCase
        value={ctaType}
        defaultComponent={
          <BaseButtonGroup
            actionButtonProps={{
              disabled: true,
            }}
            onClickShare={handleClickShare}
          />
        }
        caseBy={{
          unjoined: (
            <ParticipateButtonGroup
              eventId={eventId}
              eventInfo={eventInfoData}
              hostInfo={hostInfo}
              afterJoin={handleClickEnterChatRoom}
              onClickShare={handleClickShare}
            />
          ),
          project_joined_event_unjoined: (
            <ParticipateButtonGroup
              eventId={eventId}
              eventInfo={eventInfoData}
              hostInfo={hostInfo}
              afterJoin={handleClickEnterChatRoom}
              onClickShare={handleClickShare}
            />
          ),
          joined: (
            <CancelButtonGroup
              eventId={eventId}
              eventInfo={eventInfoData}
              isEventClosed={isEventClosed}
              onClickChatRoom={handleClickEnterChatRoom}
              onClickShare={handleClickShare}
            />
          ),
          project_joined_event_joined: (
            <CancelButtonGroup
              eventId={eventId}
              eventInfo={eventInfoData}
              isEventClosed={isEventClosed}
              onClickChatRoom={handleClickEnterChatRoom}
              onClickShare={handleClickShare}
            />
          ),
          project_unjoined_event_unjoined: (
            <BaseButtonGroup
              actionButtonProps={{
                onClick: handleClickViewEditionDetail,
              }}
              buttonContent="View edition details"
              onClickShare={handleClickShare}
            />
          ),
          host: (
            <HostButtonGroup
              onClickChatRoom={handleClickEnterChatRoom}
              onClickShare={handleClickShare}
            />
          ),
          host_closed: (
            <HostClosedButtonGroup
              eventId={eventId}
              onClickChatRoom={handleClickEnterChatRoom}
              onClickShare={handleClickShare}
            />
          ),
          joined_closed: (
            <JoinedClosedButtonGroup
              eventId={eventId}
              eventInfo={eventInfoData}
              hostInfo={hostInfo}
              onClickChatRoom={handleClickEnterChatRoom}
              onClickShare={handleClickShare}
            />
          ),
          closed: (
            <BaseButtonGroup
              actionButtonProps={{
                disabled: true,
              }}
              buttonContent="Closed"
              onClickShare={handleClickShare}
            />
          ),
          full: (
            <BaseButtonGroup
              actionButtonProps={{
                disabled: true,
              }}
              buttonContent="Full"
              onClickShare={handleClickShare}
            />
          ),
        }}
      />
    </S.BottomButtonBox>
  );
};

export default EventDetailButton;
